import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Space, Spin, Tooltip} from 'antd';
import {LoadingOutlined} from '@ant-design/icons';
import moment from 'moment';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {
  StyledBillingInformation,
  StyledBillingInformationTitle,
  StyledBillingInformationTitleSpace,
  StyledBillingInformationTable,
  StyledBillingInformationLoadMoreContainer,
  StyledBillingInformationTableDownloadContainer
} from './StyledBillingInformation';
import {Invoice2Icon} from '../../../../../icons';
import {billingActions} from '../../../../../state/actions/billing.actions';
import {amountHelpers, dateHelpers, fileHelpers, systemHelpers} from '../../../../../utils/helpers';
import {firebaseEvents} from '../../../../../snippets/firebase';

const dateFormat = 'MMM YYYY';
const tableRowKey = 'transfer_id';

const BillingInformation = ({
  data,
  handleLoadMore,
  loading = false,
  pagination,
  ...rest
}) => {
  const [trans] = useTranslation(['main', 'settings']);
  const t = (key) => trans(`settings:tabs.billing.${key}`);
  const [selectedTransferId, setSelectedTransferId] = useState(undefined);
  const dispatch = useDispatch();

  const renewsData = useMemo(() => moment().add(1, 'M').format(dateFormat), []);

  const columns = [
    {
      key: 'created_date',
      dataIndex: 'created_date',
      title: trans('date'),
      render: (value) => (
        <span className='text-uppercase'>
          {dateHelpers.getTimestampDate(value, dateFormat)}
        </span>
      )
    },
    {
      align: 'right',
      key: 'cashback',
      dataIndex: 'cashback',
      title: t('cashback'),
      render: (value) => value ? amountHelpers.getAmountWithCurrencyCode(value) : '-',
    },
    {
      align: 'right',
      key: 'amount',
      dataIndex: 'amount',
      title: t('cashOut'),
      render: (value) => (
        <span className='cashout'>
          {amountHelpers.getAmountWithCurrencyCode(-value)}
        </span>
      )
    },
    {
      dataIndex: 'match',
      title: '',
      render: (_, record) => {
        const id = record[tableRowKey];
        const fileUrl = record.file_url;
        return (
          <StyledBillingInformationTableDownloadContainer>
            {fileUrl && (
              <Space size={2}>
                <Spin indicator={<LoadingOutlined spin />} spinning={selectedTransferId === id} />
                <Tooltip title={trans('clickHereToDownloadFile')}>
                  <Invoice2Icon onClick={(e) => onDownload(e, id)} />
                </Tooltip>
              </Space>
            )}
          </StyledBillingInformationTableDownloadContainer>
        )
      },
      width: 32
    },
  ];

  const title = (
    <StyledBillingInformationTitle>
      <StyledBillingInformationTitleSpace direction='vertical' size={4}>
        <span className='billing-title'>{t('yourPlan')}</span>
        <span className='billing-plan'>{t('proMonthly')}</span>
        <span className='billing-renews-date'>{t('renewsOn')} {renewsData}</span>
      </StyledBillingInformationTitleSpace>
    </StyledBillingInformationTitle>
  );

  const onDownload = (e, id) => {
    e.stopPropagation();
    if (id === undefined) return;

    setSelectedTransferId(id);
    dispatch(billingActions.getTransferAttachmentFile({
      id,
      successCallback: (data) => {
        fileHelpers.saveFile(data);
        setSelectedTransferId(undefined);
        systemHelpers.logEvent(firebaseEvents.COMPANY_SETTINGS_DOWNLOAD_BILLING);
      },
      errorCallback: () => setSelectedTransferId(undefined)
    }));
  }

  return (
    <StyledBillingInformation
      title={title}
      {...rest}
    >
      <StyledBillingInformationTable
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        rowKey='created_date'
      />
      {pagination?.cursor && (
        <StyledBillingInformationLoadMoreContainer>
          <Button
            size='large'
            loading={loading}
            onClick={handleLoadMore}
          >
            {trans('loadMore')}
          </Button>
        </StyledBillingInformationLoadMoreContainer>
      )}
    </StyledBillingInformation>
  );
}

BillingInformation.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      created_date: PropTypes.number
    })
  ),
  handleLoadMore: PropTypes.func,
  loading: PropTypes.bool,
  pagination: PropTypes.objectOf(
    PropTypes.shape({
      cursor: PropTypes.string,
      next_page: PropTypes.string
    })
  )
}

export default BillingInformation;
